import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import API, { formatWIB, formatWIBDate, formatWIBTime } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  PaperPlaneTilt, Plus, Trash, Robot, User, CircleNotch,
  ChatDots, Wrench, Lightbulb, ArrowRight, ArrowSquareOut,
  CheckCircle, MagnifyingGlass, PushPin, Pencil, Copy,
  ArrowClockwise, Stop, DownloadSimple, Rows, ListDashes,
  Lightning, MagicWand, Tag, CheckSquare, Square, Funnel, X,
  Clock, Gear, FloppyDisk,
} from "@phosphor-icons/react";

/* ──────────────────────────── HELPERS ───────────────────────────── */
const JOB_ID_REGEX = /\b([0-9a-f]{8})\b/g;

function extractQuickActions(text) {
  const actions = [], seen = new Set();
  let m;
  while ((m = JOB_ID_REGEX.exec(text)) !== null) {
    if (seen.has(m[1])) continue;
    seen.add(m[1]);
    actions.push({ type: "job", id: m[1], label: `Buka job ${m[1]}` });
    if (actions.length >= 4) break;
  }
  return actions;
}

const MD = {
  p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="text-sm">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-zinc-900">{children}</strong>,
  code: ({ inline, children }) => inline
    ? <code className="px-1 py-0.5 rounded bg-zinc-100 text-violet-700 text-[11px] font-mono">{children}</code>
    : <pre className="bg-zinc-900 text-zinc-100 p-2 rounded-sm text-[11px] font-mono overflow-x-auto my-2"><code>{children}</code></pre>,
  table: ({ children }) => <div className="overflow-x-auto my-2"><table className="text-[11px] border border-zinc-200 rounded-sm">{children}</table></div>,
  th: ({ children }) => <th className="px-2 py-1 bg-zinc-100 border-b border-zinc-200 text-left font-semibold">{children}</th>,
  td: ({ children }) => <td className="px-2 py-1 border-b border-zinc-100">{children}</td>,
  h1: ({ children }) => <h3 className="text-sm font-semibold text-zinc-900 mt-2 mb-1">{children}</h3>,
  h2: ({ children }) => <h4 className="text-sm font-semibold text-zinc-800 mt-2 mb-1">{children}</h4>,
  h3: ({ children }) => <h5 className="text-xs font-semibold text-zinc-800 mt-2 mb-1">{children}</h5>,
};

/* Template prompts categorized */
const TEMPLATE_GROUPS = {
  audit: {
    label: "Audit Harian",
    icon: Lightning,
    prompts: [
      "Kondisi rekonsiliasi hari ini bagaimana? Kasih ringkasan lengkap.",
      "Ada anomali PG atau pola fraud yang terdeteksi?",
      "Regenerate daily briefing untuk hari ini.",
      "Tampilkan 5 job terakhir beserta match rate-nya.",
    ],
  },
  fraud: {
    label: "Investigasi Fraud",
    icon: MagicWand,
    prompts: [
      "Deteksi pola fraud gabungan dari semua data.",
      "Apakah ada smurfing — rekening yang dipakai banyak member?",
      "Audit adjustment vague (notes < 10 karakter) bulan ini.",
      "Validasi semua PINDAH DANA KE X dari KAS bulan ini.",
    ],
  },
  pulsa: {
    label: "JUAL PULSA",
    icon: Tag,
    prompts: [
      "Rekonsiliasi JUAL PULSA semua site bulan ini, yang BELUM TT berapa banyak?",
      "Validasi JUAL PULSA dengan verifikasi dua lapis ke tab DEPO BANK.",
      "Audit staf JUAL PULSA yang paling sering BELUM TT.",
      "Breakdown JUAL PULSA per staf & site.",
    ],
  },
  kas: {
    label: "KAS Perusahaan",
    icon: ChatDots,
    prompts: [
      "Tampilkan ringkasan KAS Perusahaan bulan ini (total IN, OUT, NET).",
      "CAIR QRISHOKI PGKUDA bulan ini totalnya berapa?",
      "Breakdown CAIR semua PG per site bulan ini.",
      "Validasi semua PINDAH DANA keluar dari KAS, apakah tiba di target?",
    ],
  },
  forensik: {
    label: "Forensik & Trace",
    icon: MagnifyingGlass,
    prompts: [
      "Trace uang Rp 1.000.000 di seluruh sistem bulan ini.",
      "Cari member dengan kata kunci X di cache.",
      "Cek profil member username X di PGDewa.",
      "Audit staf [nama] di semua site bulan ini.",
    ],
  },
};

/* Slash commands */
const SLASH_CMDS = [
  { cmd: "/audit", label: "Kondisi harian", prompt: "Kondisi rekonsiliasi hari ini bagaimana?" },
  { cmd: "/fraud", label: "Deteksi fraud", prompt: "Deteksi pola fraud gabungan dari semua data." },
  { cmd: "/pulsa", label: "Rekonsiliasi JUAL PULSA", prompt: "Rekonsiliasi JUAL PULSA semua site bulan ini." },
  { cmd: "/kas", label: "Ringkasan KAS", prompt: "Ringkasan KAS Perusahaan bulan ini." },
  { cmd: "/trace", label: "Trace uang", prompt: "Trace uang Rp [amount] di seluruh sistem." },
  { cmd: "/member", label: "Lookup member", prompt: "Cek profil member [username] di site [X]." },
  { cmd: "/smurfing", label: "Deteksi smurfing", prompt: "Deteksi rekening yang dipakai multiple member." },
  { cmd: "/verify", label: "Verify PINDAH DANA", prompt: "Validasi semua PINDAH DANA KE X dari KAS bulan ini." },
  { cmd: "/briefing", label: "Daily Briefing", prompt: "Generate daily briefing untuk hari ini." },
];

const TAG_COLORS = {
  audit: "bg-blue-100 text-blue-700",
  fraud: "bg-red-100 text-red-700",
  pulsa: "bg-amber-100 text-amber-700",
  kas: "bg-emerald-100 text-emerald-700",
  trace: "bg-violet-100 text-violet-700",
  member: "bg-cyan-100 text-cyan-700",
  job: "bg-zinc-100 text-zinc-700",
};

/* ──────────────────────────── BUBBLES ───────────────────────────── */
function UserBubble({ content, timestamp, onCopy }) {
  return (
    <div data-testid="msg-user" className="flex gap-3 flex-row-reverse group">
      <div className="w-7 h-7 rounded-sm flex items-center justify-center shrink-0 bg-zinc-800">
        <User size={14} weight="bold" className="text-white" />
      </div>
      <div className="max-w-[75%] text-right">
        <div className="px-3 py-2.5 rounded-sm text-sm leading-relaxed whitespace-pre-line bg-zinc-100 text-zinc-800">
          {content}
        </div>
        <div className="flex items-center justify-end gap-1 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onCopy(content)} className="p-0.5 hover:bg-zinc-100 rounded-sm" title="Copy">
            <Copy size={10} className="text-zinc-400" />
          </button>
          <p className="text-[9px] text-zinc-400 font-mono">
            {timestamp ? formatWIBTime(timestamp) : ""}
          </p>
        </div>
      </div>
    </div>
  );
}

function AssistantBubble({ msg, onNavigate, onSuggestionClick, onCopy, onRegenerate, onTogglePin, pinned }) {
  const quickActions = msg.content ? extractQuickActions(msg.content) : [];
  return (
    <div data-testid="msg-assistant" className="flex gap-3 group">
      <div className="w-7 h-7 rounded-sm flex items-center justify-center shrink-0 bg-violet-600">
        <Robot size={14} weight="bold" className="text-white" />
      </div>
      <div className="max-w-[78%] space-y-1.5">
        <div className="px-3 py-2.5 rounded-sm text-sm text-zinc-800 bg-violet-50 border border-violet-100 relative">
          <div className="prose prose-sm max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{msg.content || ""}</ReactMarkdown>
          </div>
          {/* Hover actions */}
          <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 rounded-sm border border-violet-100 px-0.5">
            <button onClick={() => onCopy(msg.content)} className="p-1 hover:bg-violet-100 rounded-sm" title="Copy">
              <Copy size={11} className="text-zinc-500" />
            </button>
            {onRegenerate && (
              <button onClick={onRegenerate} className="p-1 hover:bg-violet-100 rounded-sm" title="Regenerate">
                <ArrowClockwise size={11} className="text-zinc-500" />
              </button>
            )}
            <button onClick={onTogglePin} className="p-1 hover:bg-violet-100 rounded-sm" title={pinned ? "Unpin" : "Pin"}>
              <PushPin size={11} weight={pinned ? "fill" : "regular"} className={pinned ? "text-amber-500" : "text-zinc-500"} />
            </button>
          </div>
        </div>

        {msg.tools_used && msg.tools_used.length > 0 && (
          <div data-testid="tools-used" className="flex flex-wrap gap-1.5 items-center">
            <Wrench size={11} className="text-zinc-400" />
            {msg.tools_used.map((t, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded-sm font-mono" title={t.name}>
                {t.label || t.name}
              </span>
            ))}
          </div>
        )}

        {quickActions.length > 0 && (
          <div data-testid="quick-actions" className="flex flex-wrap gap-1.5 items-center pt-0.5">
            {quickActions.map((a, i) => (
              <button key={i} onClick={() => onNavigate(`/jobs/${a.id}`)}
                className="text-[10px] px-2 py-0.5 bg-white border border-zinc-300 rounded-sm hover:border-violet-400 hover:bg-violet-50 text-zinc-700 flex items-center gap-1 transition-colors">
                <ArrowSquareOut size={10} /> {a.label}
              </button>
            ))}
          </div>
        )}

        {msg.suggestions && msg.suggestions.length > 0 && (
          <div data-testid="suggestions" className="flex flex-col gap-1 pt-1">
            <div className="flex items-center gap-1 text-[10px] text-zinc-500 font-medium">
              <Lightbulb size={11} className="text-amber-500" /> Lanjutan:
            </div>
            {msg.suggestions.map((s, i) => (
              <button key={i} onClick={() => onSuggestionClick(s)}
                className="text-left text-[11px] px-2 py-1.5 bg-white border border-zinc-200 rounded-sm hover:border-violet-400 hover:bg-violet-50 text-zinc-700 flex items-center justify-between gap-2 group transition-colors">
                <span>{s}</span>
                <ArrowRight size={11} className="text-zinc-400 group-hover:text-violet-600 shrink-0" />
              </button>
            ))}
          </div>
        )}
        <p className="text-[9px] text-zinc-400 font-mono">
          {msg.timestamp ? formatWIBTime(msg.timestamp) : ""}
        </p>
      </div>
    </div>
  );
}

function StreamingBubble({ text, toolEvents, thinkingMsg, onStop }) {
  return (
    <div data-testid="streaming-bubble" className="flex gap-3">
      <div className="w-7 h-7 rounded-sm flex items-center justify-center shrink-0 bg-violet-600 animate-pulse">
        <Robot size={14} weight="bold" className="text-white" />
      </div>
      <div className="max-w-[78%] space-y-1.5">
        {toolEvents.length > 0 && (
          <div className="text-[10px] text-zinc-500 space-y-0.5">
            {toolEvents.map((ev, i) => (
              <div key={i} className="flex items-center gap-1.5">
                {ev.status === "done" ? <CheckCircle size={11} weight="fill" className="text-emerald-500" />
                  : <CircleNotch size={11} className="animate-spin text-violet-500" />}
                <span>{ev.label}</span>
              </div>
            ))}
          </div>
        )}
        {thinkingMsg && !text && (
          <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
            <CircleNotch size={11} className="animate-spin text-violet-500" />
            <span>{thinkingMsg}</span>
          </div>
        )}
        {text && (
          <div className="px-3 py-2.5 rounded-sm text-sm text-zinc-800 bg-violet-50 border border-violet-100">
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{text}</ReactMarkdown>
            </div>
            <span className="inline-block w-1.5 h-3 bg-violet-500 align-middle animate-pulse ml-0.5" />
          </div>
        )}
        <button onClick={onStop} data-testid="stop-btn"
          className="text-[10px] flex items-center gap-1 px-2 py-1 bg-white border border-red-200 text-red-600 rounded-sm hover:bg-red-50">
          <Stop size={11} weight="fill" /> Hentikan
        </button>
      </div>
    </div>
  );
}

/* ──────────────────────────── SESSION ITEM ───────────────────────────── */
function SessionItem({ session, active, selected, selectMode, onClick, onToggleSelect, onDelete, onRename, onTogglePin }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const dateStr = session.updated_at
    ? formatWIBDate(session.updated_at)
    : "";
  return (
    <div data-testid={`session-item-${session.session_id}`}
      className={`flex items-start gap-2 px-3 py-2 cursor-pointer text-xs transition-colors group border-l-2 ${
        active ? "bg-violet-50 border-violet-500" : selected ? "bg-amber-50 border-amber-400" : "hover:bg-zinc-50 border-transparent"
      }`}
      onClick={selectMode ? onToggleSelect : onClick}>
      {selectMode ? (
        <button onClick={(e) => { e.stopPropagation(); onToggleSelect(); }} className="mt-0.5 shrink-0">
          {selected ? <CheckSquare size={14} weight="fill" className="text-amber-500" /> : <Square size={14} className="text-zinc-400" />}
        </button>
      ) : (
        <ChatDots size={14} className="text-zinc-400 shrink-0 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          {session.pinned && <PushPin size={10} weight="fill" className="text-amber-500 shrink-0" />}
          <p className="truncate text-zinc-800 font-medium">{session.title || "Percakapan baru"}</p>
        </div>
        {session.preview && <p className="truncate text-[10px] text-zinc-500 mt-0.5">{session.preview}</p>}
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[9px] text-zinc-400 font-mono">{dateStr}</span>
          {session.message_count > 0 && <span className="text-[9px] text-zinc-400">· {session.message_count} pesan</span>}
          {(session.tags || []).slice(0, 2).map((tag, i) => (
            <span key={i} className={`text-[9px] px-1 rounded-sm ${TAG_COLORS[tag] || "bg-zinc-100 text-zinc-600"}`}>{tag}</span>
          ))}
        </div>
      </div>
      {!selectMode && (
        <div className="relative shrink-0">
          <button onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-zinc-100 rounded-sm text-zinc-400" data-testid={`session-menu-${session.session_id}`}>
            ⋯
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); }} />
              <div className="absolute right-0 top-6 bg-white border border-zinc-200 rounded-sm shadow-lg z-20 text-[11px] min-w-[120px]">
                <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onTogglePin(); }}
                  className="w-full text-left px-2 py-1.5 hover:bg-zinc-50 flex items-center gap-1.5">
                  <PushPin size={11} /> {session.pinned ? "Unpin" : "Pin"}
                </button>
                <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onRename(); }}
                  className="w-full text-left px-2 py-1.5 hover:bg-zinc-50 flex items-center gap-1.5">
                  <Pencil size={11} /> Rename
                </button>
                <button onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(); }}
                  className="w-full text-left px-2 py-1.5 hover:bg-red-50 text-red-600 flex items-center gap-1.5">
                  <Trash size={11} /> Hapus
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────── MAIN PAGE ───────────────────────────── */
export default function AiAssistantPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [streamText, setStreamText] = useState("");
  const [streamThinking, setStreamThinking] = useState("");
  const [streamTools, setStreamTools] = useState([]);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [compact, setCompact] = useState(() => localStorage.getItem("ai-compact") === "1");
  const [templateOpen, setTemplateOpen] = useState("audit");
  const [slashOpen, setSlashOpen] = useState(false);
  const [pinnedMsgs, setPinnedMsgs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ai-pinned") || "{}"); } catch { return {}; }
  });
  const [recentPrompts, setRecentPrompts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ai-recent-prompts") || "[]"); } catch { return []; }
  });
  const [customCmds, setCustomCmds] = useState([]);
  const [cmdModalOpen, setCmdModalOpen] = useState(false);
  const [editingCmd, setEditingCmd] = useState(null); // null = create, {id,cmd,label,prompt} = edit
  const [cmdForm, setCmdForm] = useState({ cmd: "", label: "", prompt: "" });
  const [cmdError, setCmdError] = useState("");

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortCtrlRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(() => { scrollToBottom(); }, [messages, streamText, streamTools]);

  const loadSessions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (tagFilter) params.set("tag", tagFilter);
      const { data } = await API.get(`/ai/sessions?${params.toString()}`);
      setSessions(data.sessions || []);
    } catch { /* silent */ }
    finally { setLoadingSessions(false); }
  }, [search, tagFilter]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const loadCustomCmds = useCallback(async () => {
    try {
      const { data } = await API.get("/ai/custom-commands");
      setCustomCmds(data.commands || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadCustomCmds(); }, [loadCustomCmds]);

  const pushRecentPrompt = (text) => {
    if (!text || text.length < 3) return;
    const snippet = text.slice(0, 120);
    setRecentPrompts(prev => {
      const filtered = prev.filter(p => p !== snippet);
      const next = [snippet, ...filtered].slice(0, 8);
      localStorage.setItem("ai-recent-prompts", JSON.stringify(next));
      return next;
    });
  };

  const clearRecentPrompts = () => {
    setRecentPrompts([]);
    localStorage.removeItem("ai-recent-prompts");
  };

  const openCreateCmdModal = () => {
    setEditingCmd(null);
    setCmdForm({ cmd: "", label: "", prompt: "" });
    setCmdError("");
    setCmdModalOpen(true);
  };
  const openEditCmdModal = (c) => {
    setEditingCmd(c);
    setCmdForm({ cmd: c.cmd, label: c.label, prompt: c.prompt });
    setCmdError("");
    setCmdModalOpen(true);
  };
  const saveCustomCmd = async () => {
    setCmdError("");
    const body = {
      cmd: cmdForm.cmd.trim(),
      label: cmdForm.label.trim(),
      prompt: cmdForm.prompt.trim(),
    };
    if (!body.cmd || !body.label || !body.prompt) {
      setCmdError("Semua field wajib diisi");
      return;
    }
    try {
      if (editingCmd) {
        await API.put(`/ai/custom-commands/${editingCmd.id}`, body);
      } else {
        await API.post("/ai/custom-commands", body);
      }
      setCmdModalOpen(false);
      loadCustomCmds();
    } catch (e) {
      setCmdError(e.response?.data?.detail || "Gagal simpan command");
    }
  };
  const deleteCustomCmd = async (id) => {
    if (!window.confirm("Hapus command ini?")) return;
    try {
      await API.delete(`/ai/custom-commands/${id}`);
      loadCustomCmds();
    } catch { alert("Gagal hapus"); }
  };

  const loadSession = async (sessionId) => {
    try {
      const { data } = await API.get(`/ai/sessions/${sessionId}`);
      setMessages(data.messages || []);
      setActiveSession(sessionId);
    } catch { setMessages([]); }
  };

  const startNewSession = () => {
    setActiveSession(null); setMessages([]); setInput(""); inputRef.current?.focus();
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm("Hapus sesi ini?")) return;
    try {
      await API.delete(`/ai/sessions/${sessionId}`);
      if (activeSession === sessionId) { setActiveSession(null); setMessages([]); }
      loadSessions();
    } catch { /* silent */ }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Hapus ${selectedIds.size} sesi terpilih?`)) return;
    try {
      await API.post("/ai/sessions/bulk-delete", { session_ids: Array.from(selectedIds) });
      if (selectedIds.has(activeSession)) { setActiveSession(null); setMessages([]); }
      setSelectedIds(new Set()); setSelectMode(false);
      loadSessions();
    } catch { alert("Gagal bulk delete"); }
  };

  const renameSession = async (sessionId, currentTitle) => {
    const newTitle = window.prompt("Judul baru:", currentTitle || "");
    if (!newTitle || !newTitle.trim()) return;
    try {
      await API.put(`/ai/sessions/${sessionId}/rename`, { title: newTitle.trim() });
      loadSessions();
    } catch { alert("Gagal rename"); }
  };

  const togglePinSession = async (sessionId) => {
    try { await API.post(`/ai/sessions/${sessionId}/pin`); loadSessions(); }
    catch { /* silent */ }
  };

  const toggleSelect = (sid) => {
    const next = new Set(selectedIds);
    if (next.has(sid)) next.delete(sid); else next.add(sid);
    setSelectedIds(next);
  };

  const toggleCompact = () => {
    const v = !compact;
    setCompact(v);
    localStorage.setItem("ai-compact", v ? "1" : "0");
  };

  const exportConversation = () => {
    if (messages.length === 0) return;
    const md = messages.map(m => {
      const ts = m.timestamp ? formatWIB(m.timestamp) : "";
      const role = m.role === "user" ? "🧑 Operator" : "🤖 AI";
      const tools = m.tools_used && m.tools_used.length
        ? `\n_Tools: ${m.tools_used.map(t => t.label || t.name).join(", ")}_`
        : "";
      return `### ${role} — ${ts}\n\n${m.content}${tools}`;
    }).join("\n\n---\n\n");
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ai-chat-${activeSession || "new"}-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyText = (txt) => {
    try { navigator.clipboard.writeText(txt); } catch { /* noop */ }
  };

  const togglePinMsg = (idx, content) => {
    const key = `${activeSession || "new"}__${idx}`;
    const copy = { ...pinnedMsgs };
    if (copy[key]) delete copy[key]; else copy[key] = content.slice(0, 200);
    setPinnedMsgs(copy);
    localStorage.setItem("ai-pinned", JSON.stringify(copy));
  };

  const sendMessage = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;

    const userMsg = { role: "user", content: text, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    pushRecentPrompt(text);
    setInput(""); setSlashOpen(false);
    setSending(true);
    setStreamText(""); setStreamThinking("AI sedang menganalisis pertanyaan..."); setStreamTools([]);

    const ctrl = new AbortController();
    abortCtrlRef.current = ctrl;

    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${BACKEND}/api/ai/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: activeSession }),
        signal: ctrl.signal,
      });

      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "", accumulatedText = "", finalMeta = null;
      const toolsLocal = [];

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const block of lines) {
          const line = block.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          try {
            const ev = JSON.parse(line.slice(6));
            if (ev.type === "thinking") setStreamThinking(ev.message);
            else if (ev.type === "tool_call") {
              toolsLocal.push({ name: ev.name, label: ev.label, status: "running" });
              setStreamTools([...toolsLocal]); setStreamThinking("");
            } else if (ev.type === "tool_result") {
              const idx = toolsLocal.findIndex((t) => t.name === ev.name && t.status === "running");
              if (idx >= 0) toolsLocal[idx].status = "done";
              setStreamTools([...toolsLocal]);
            } else if (ev.type === "content_delta") {
              accumulatedText += ev.text; setStreamText(accumulatedText); setStreamThinking("");
            } else if (ev.type === "done") finalMeta = ev;
            else if (ev.type === "error") throw new Error(ev.message);
          } catch (e) { console.warn("SSE parse err:", e); }
        }
      }

      const aiMsg = {
        role: "assistant", content: accumulatedText,
        timestamp: finalMeta?.timestamp || new Date().toISOString(),
        tools_used: finalMeta?.tools_used || [],
        suggestions: finalMeta?.suggestions || [],
      };
      setMessages((prev) => [...prev, aiMsg]);
      if (!activeSession && finalMeta?.session_id) setActiveSession(finalMeta.session_id);
      loadSessions();
    } catch (e) {
      if (e.name === "AbortError") {
        setMessages((prev) => [...prev, { role: "assistant", content: "_(Dihentikan oleh user)_", timestamp: new Date().toISOString() }]);
      } else {
        setMessages((prev) => [...prev, { role: "assistant", content: `Maaf, terjadi error: ${e.message}`, timestamp: new Date().toISOString() }]);
      }
    } finally {
      setSending(false); setStreamText(""); setStreamThinking(""); setStreamTools([]);
      abortCtrlRef.current = null;
    }
  };

  const stopStreaming = () => {
    abortCtrlRef.current?.abort();
  };

  const regenerate = () => {
    if (messages.length < 2) return;
    // Remove last assistant msg, re-send last user
    const lastUserIdx = [...messages].reverse().findIndex(m => m.role === "user");
    if (lastUserIdx < 0) return;
    const actualIdx = messages.length - 1 - lastUserIdx;
    const lastUser = messages[actualIdx];
    setMessages(messages.slice(0, actualIdx));
    sendMessage(lastUser.content);
  };

  /* ─────── KEYBOARD SHORTCUTS ─────── */
  useEffect(() => {
    const handler = (e) => {
      const ctrl = e.metaKey || e.ctrlKey;
      if (ctrl && e.key.toLowerCase() === "k") {
        e.preventDefault();
        document.querySelector('[data-testid="session-search"]')?.focus();
      } else if (ctrl && e.key.toLowerCase() === "n") {
        e.preventDefault();
        startNewSession();
      } else if (e.key === "Escape") {
        if (sending) stopStreaming();
        else if (slashOpen) setSlashOpen(false);
      } else if (e.key === "ArrowUp" && document.activeElement === inputRef.current && !input) {
        const lastUser = [...messages].reverse().find(m => m.role === "user");
        if (lastUser) { e.preventDefault(); setInput(lastUser.content); }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sending, slashOpen, messages, input]);

  /* ─────── INPUT HANDLERS ─────── */
  const onInputChange = (e) => {
    const v = e.target.value;
    setInput(v);
    setSlashOpen(v.startsWith("/") && v.length <= 15);
  };

  const onInputKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const slashFiltered = input.startsWith("/")
    ? [
        ...SLASH_CMDS,
        ...customCmds.map(c => ({ cmd: c.cmd, label: c.label, prompt: c.prompt, _custom: true, _id: c.id })),
      ].filter(c => c.cmd.startsWith(input.toLowerCase().split(" ")[0]))
    : [];

  const pinnedMsgsArray = Object.entries(pinnedMsgs).filter(([k]) => k.startsWith((activeSession || "new") + "__"));

  const allTags = ["audit", "fraud", "pulsa", "kas", "trace", "member", "job"];

  return (
    <div data-testid="ai-assistant-page" className="flex h-[calc(100vh-80px)] gap-0">
      {/* ─────── SIDEBAR ─────── */}
      <div className="w-72 border-r border-zinc-200 flex flex-col bg-white shrink-0">
        <div className="px-3 py-3 border-b border-zinc-200 space-y-2">
          <Button data-testid="new-chat-btn" onClick={startNewSession} className="w-full text-xs h-8 gap-1.5 bg-violet-600 text-white hover:bg-violet-700">
            <Plus size={14} /> Percakapan Baru <span className="text-[9px] text-violet-200 ml-auto font-mono">⌘N</span>
          </Button>
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2 top-2.5 text-zinc-400" />
            <input data-testid="session-search" type="text" placeholder="Cari sesi... ⌘K" value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-7 pr-2 py-1.5 border border-zinc-200 rounded-sm text-[11px] outline-none focus:border-violet-400" />
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <button onClick={() => setTagFilter("")}
              className={`text-[9px] px-1.5 py-0.5 rounded-sm ${!tagFilter ? "bg-zinc-800 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
              Semua
            </button>
            {allTags.map(t => (
              <button key={t} onClick={() => setTagFilter(tagFilter === t ? "" : t)}
                className={`text-[9px] px-1.5 py-0.5 rounded-sm ${tagFilter === t ? TAG_COLORS[t] + " font-semibold" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
                {t}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <button onClick={() => { setSelectMode(!selectMode); setSelectedIds(new Set()); }} data-testid="select-mode-toggle"
              className={`px-2 py-0.5 rounded-sm ${selectMode ? "bg-amber-100 text-amber-700" : "text-zinc-500 hover:bg-zinc-100"}`}>
              <ListDashes size={11} className="inline mr-0.5" /> Pilih
            </button>
            {selectMode && selectedIds.size > 0 && (
              <button data-testid="bulk-delete-btn" onClick={bulkDelete} className="px-2 py-0.5 bg-red-600 text-white rounded-sm hover:bg-red-700">
                <Trash size={11} className="inline mr-0.5" /> Hapus {selectedIds.size}
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Recently Used section */}
          {recentPrompts.length > 0 && !search && !tagFilter && (
            <div data-testid="recent-prompts" className="border-b border-zinc-100 pb-1.5">
              <div className="flex items-center justify-between px-3 pt-2 pb-1">
                <div className="flex items-center gap-1 text-[10px] font-semibold text-zinc-500 uppercase tracking-wide">
                  <Clock size={10} /> Baru Digunakan
                </div>
                <button onClick={clearRecentPrompts} className="text-[9px] text-zinc-400 hover:text-red-500" data-testid="clear-recent-btn">Bersihkan</button>
              </div>
              {recentPrompts.slice(0, 5).map((p, i) => (
                <button key={i} data-testid={`recent-prompt-${i}`} onClick={() => sendMessage(p)}
                  className="w-full text-left px-3 py-1 text-[11px] text-zinc-600 hover:bg-violet-50 hover:text-violet-700 truncate transition-colors flex items-center gap-1.5">
                  <ArrowClockwise size={10} className="text-zinc-400 shrink-0" />
                  <span className="truncate">{p}</span>
                </button>
              ))}
            </div>
          )}

          {/* Custom Commands section */}
          {customCmds.length > 0 && !search && !tagFilter && (
            <div data-testid="custom-cmds-sidebar" className="border-b border-zinc-100 pb-1.5">
              <div className="flex items-center justify-between px-3 pt-2 pb-1">
                <div className="flex items-center gap-1 text-[10px] font-semibold text-zinc-500 uppercase tracking-wide">
                  <MagicWand size={10} /> Custom Slash
                </div>
                <button onClick={openCreateCmdModal} className="text-[9px] text-violet-500 hover:text-violet-700" data-testid="add-custom-cmd-btn">
                  + Tambah
                </button>
              </div>
              {customCmds.slice(0, 5).map((c) => (
                <button key={c.id} data-testid={`custom-cmd-${c.id}`} onClick={() => sendMessage(c.prompt)}
                  className="w-full text-left px-3 py-1 text-[11px] text-zinc-600 hover:bg-violet-50 truncate transition-colors flex items-center gap-1.5 group">
                  <span className="font-mono text-violet-600 text-[10px] shrink-0">{c.cmd}</span>
                  <span className="truncate flex-1">{c.label}</span>
                  <button onClick={(e) => { e.stopPropagation(); openEditCmdModal(c); }}
                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-zinc-200 rounded-sm" title="Edit">
                    <Pencil size={9} className="text-zinc-400" />
                  </button>
                </button>
              ))}
              {customCmds.length > 5 && (
                <button onClick={openCreateCmdModal} className="w-full text-[9px] text-zinc-400 hover:text-violet-600 py-1" data-testid="manage-cmds-btn">
                  Kelola semua ({customCmds.length}) →
                </button>
              )}
            </div>
          )}
          {customCmds.length === 0 && !search && !tagFilter && (
            <button onClick={openCreateCmdModal} data-testid="create-first-cmd-btn"
              className="w-full text-left px-3 py-2 border-b border-zinc-100 text-[11px] text-zinc-500 hover:bg-violet-50 hover:text-violet-700 flex items-center gap-1.5">
              <MagicWand size={11} /> Buat custom slash command…
            </button>
          )}

          {loadingSessions ? <p className="text-xs text-zinc-400 text-center py-4 font-mono">Loading...</p>
            : sessions.length === 0 ? <p className="text-xs text-zinc-400 text-center py-8">Tidak ada sesi</p>
            : sessions.map(s => (
              <SessionItem key={s.session_id} session={s}
                active={activeSession === s.session_id}
                selected={selectedIds.has(s.session_id)}
                selectMode={selectMode}
                onClick={() => loadSession(s.session_id)}
                onToggleSelect={() => toggleSelect(s.session_id)}
                onDelete={() => deleteSession(s.session_id)}
                onRename={() => renameSession(s.session_id, s.title)}
                onTogglePin={() => togglePinSession(s.session_id)} />
            ))}
        </div>
      </div>

      {/* ─────── MAIN CHAT ─────── */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-4 py-3 border-b border-zinc-200 bg-white flex items-center gap-2">
          <Robot size={20} className="text-violet-600" />
          <div className="flex-1">
            <h1 className="text-sm font-semibold text-zinc-800">AI Asisten Rekonsiliasi</h1>
            <p className="text-[10px] text-zinc-500">GPT-4o-mini | 25 tools | Read-only</p>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button data-testid="export-btn" onClick={exportConversation} className="text-[10px] px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-50 text-zinc-600 flex items-center gap-1" title="Export ke Markdown">
                <DownloadSimple size={11} /> Export
              </button>
            )}
            <button data-testid="density-toggle" onClick={toggleCompact} className="text-[10px] px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-50 text-zinc-600 flex items-center gap-1" title={compact ? "Compact mode" : "Comfortable mode"}>
              <Rows size={11} /> {compact ? "Compact" : "Comfortable"}
            </button>
          </div>
        </div>

        {/* Pinned messages bar */}
        {pinnedMsgsArray.length > 0 && (
          <div data-testid="pinned-bar" className="px-4 py-2 bg-amber-50/50 border-b border-amber-100 flex items-start gap-2 text-[10px]">
            <PushPin size={11} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
            <div className="flex-1 flex flex-wrap gap-1">
              {pinnedMsgsArray.map(([key, snippet]) => (
                <span key={key} className="px-1.5 py-0.5 bg-white border border-amber-200 rounded-sm text-zinc-700 truncate max-w-xs" title={snippet}>{snippet.slice(0, 40)}…</span>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        <div className={`flex-1 overflow-y-auto bg-zinc-50/50 ${compact ? "px-3 py-2 space-y-2" : "px-4 py-4 space-y-4"}`}>
          {messages.length === 0 && !sending ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Robot size={48} className="text-violet-300 mb-4" />
              <h2 className="text-lg font-semibold text-zinc-700 mb-1">Halo, {user?.name || "Operator"}!</h2>
              <p className="text-sm text-zinc-500 mb-4 max-w-md">
                Saya AI Asisten rekonsiliasi. Pilih template di bawah atau ketik pertanyaan Anda.
                Gunakan <kbd className="px-1 py-0.5 bg-zinc-100 rounded text-[10px] font-mono">/</kbd> untuk slash commands.
              </p>

              {/* Template tabs */}
              <div className="w-full max-w-2xl">
                <div className="flex gap-1 mb-2 flex-wrap justify-center">
                  {Object.entries(TEMPLATE_GROUPS).map(([key, grp]) => {
                    const Icon = grp.icon;
                    return (
                      <button key={key} data-testid={`template-tab-${key}`} onClick={() => setTemplateOpen(key)}
                        className={`text-[11px] px-3 py-1.5 rounded-sm flex items-center gap-1 ${
                          templateOpen === key ? "bg-violet-600 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-violet-300"
                        }`}>
                        <Icon size={12} /> {grp.label}
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 gap-1.5">
                  {TEMPLATE_GROUPS[templateOpen].prompts.map((p, i) => (
                    <button key={i} data-testid={`template-${templateOpen}-${i}`}
                      onClick={() => sendMessage(p)}
                      className="text-left px-3 py-2 border border-zinc-200 rounded-sm text-xs text-zinc-600 bg-white hover:bg-violet-50 hover:border-violet-300 transition-colors">
                      <ChatDots size={11} className="inline mr-1.5 text-violet-400" />{p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, i) => msg.role === "user"
                ? <UserBubble key={i} content={msg.content} timestamp={msg.timestamp} onCopy={copyText} />
                : <AssistantBubble key={i} msg={msg} onNavigate={navigate} onSuggestionClick={(s) => sendMessage(s)}
                    onCopy={copyText}
                    onRegenerate={i === messages.length - 1 ? regenerate : null}
                    onTogglePin={() => togglePinMsg(i, msg.content)}
                    pinned={!!pinnedMsgs[`${activeSession || "new"}__${i}`]} />
              )}
              {sending && <StreamingBubble text={streamText} toolEvents={streamTools} thinkingMsg={streamThinking} onStop={stopStreaming} />}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-zinc-200 bg-white relative">
          {/* Slash dropdown */}
          {slashOpen && slashFiltered.length > 0 && (
            <div data-testid="slash-menu" className="absolute bottom-full left-4 right-4 mb-1 bg-white border border-zinc-200 rounded-sm shadow-lg max-h-64 overflow-y-auto z-30">
              {slashFiltered.map((c) => (
                <div key={c.cmd + (c._id || "")} className="flex items-center border-b border-zinc-100 last:border-0 hover:bg-violet-50 group">
                  <button onClick={() => { setInput(c.prompt); setSlashOpen(false); inputRef.current?.focus(); }}
                    className="flex-1 text-left px-3 py-2 text-[11px]">
                    <span className="font-mono text-violet-600 font-semibold">{c.cmd}</span>
                    {c._custom && <span className="ml-1 text-[9px] px-1 bg-amber-100 text-amber-700 rounded-sm">CUSTOM</span>}
                    <span className="text-zinc-500 ml-2">{c.label}</span>
                    <p className="text-[10px] text-zinc-400 mt-0.5 truncate">{c.prompt}</p>
                  </button>
                  {c._custom && (
                    <button onClick={(e) => { e.stopPropagation(); setSlashOpen(false); const full = customCmds.find(x => x.id === c._id); if (full) openEditCmdModal(full); }}
                      className="opacity-0 group-hover:opacity-100 p-2 hover:bg-violet-100 rounded-sm mr-1" title="Edit">
                      <Pencil size={11} className="text-zinc-500" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input ref={inputRef} data-testid="chat-input" value={input} onChange={onInputChange} onKeyDown={onInputKeyDown}
              placeholder="Tanyakan atau ketik / untuk slash commands..." className="flex-1 text-sm h-10" disabled={sending} />
            <Button data-testid="send-btn" onClick={() => sendMessage()} disabled={sending || !input.trim()}
              className="h-10 w-10 p-0 bg-violet-600 hover:bg-violet-700 text-white">
              {sending ? <CircleNotch size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} weight="bold" />}
            </Button>
          </div>
          <div className="flex items-center justify-between mt-1 text-[9px] text-zinc-400">
            <span>⌘K cari · ⌘N baru · ↑ edit terakhir · ESC hentikan</span>
            {input.length > 0 && <span>{input.length} char</span>}
          </div>
        </div>
      </div>

      {/* ─────── CUSTOM COMMAND MODAL ─────── */}
      {cmdModalOpen && (
        <div data-testid="cmd-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setCmdModalOpen(false)}>
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
              <div className="flex items-center gap-2">
                <MagicWand size={16} className="text-violet-600" />
                <h2 className="text-sm font-semibold text-zinc-800">
                  {editingCmd ? "Edit Custom Command" : "Tambah Custom Slash Command"}
                </h2>
              </div>
              <button onClick={() => setCmdModalOpen(false)} className="p-1 hover:bg-zinc-100 rounded-sm" data-testid="cmd-modal-close">
                <X size={14} className="text-zinc-500" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[10px] font-semibold text-zinc-600 uppercase tracking-wide">Command</label>
                <Input data-testid="cmd-input-cmd" value={cmdForm.cmd}
                  onChange={(e) => setCmdForm(f => ({ ...f, cmd: e.target.value }))}
                  placeholder="/mypulsa"
                  className="mt-1 text-sm h-9 font-mono" />
                <p className="text-[10px] text-zinc-400 mt-1">Awalan `/`, lowercase, huruf/angka/dash. Hindari bentrok dengan built-in.</p>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-zinc-600 uppercase tracking-wide">Label (Deskripsi Pendek)</label>
                <Input data-testid="cmd-input-label" value={cmdForm.label}
                  onChange={(e) => setCmdForm(f => ({ ...f, label: e.target.value }))}
                  placeholder="Cek pulsa site saya"
                  className="mt-1 text-sm h-9" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-zinc-600 uppercase tracking-wide">Prompt (Pesan yang Dikirim)</label>
                <textarea data-testid="cmd-input-prompt" value={cmdForm.prompt}
                  onChange={(e) => setCmdForm(f => ({ ...f, prompt: e.target.value }))}
                  placeholder="Rekonsiliasi JUAL PULSA site PGRaja & PGDewa bulan ini, breakdown per staf."
                  rows={4}
                  className="mt-1 w-full text-sm p-2 border border-zinc-200 rounded-sm outline-none focus:border-violet-400 resize-none" />
              </div>
              {cmdError && <p data-testid="cmd-error" className="text-[11px] text-red-600 bg-red-50 border border-red-100 px-2 py-1 rounded-sm">{cmdError}</p>}

              {customCmds.length > 0 && !editingCmd && (
                <div className="border-t border-zinc-100 pt-3">
                  <p className="text-[10px] font-semibold text-zinc-600 uppercase mb-1.5 tracking-wide">Command tersimpan ({customCmds.length})</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {customCmds.map(c => (
                      <div key={c.id} className="flex items-center gap-2 text-[11px] px-2 py-1.5 bg-zinc-50 rounded-sm group">
                        <span className="font-mono text-violet-600 font-semibold shrink-0">{c.cmd}</span>
                        <span className="text-zinc-600 truncate flex-1">{c.label}</span>
                        <button onClick={() => openEditCmdModal(c)} className="p-1 hover:bg-zinc-200 rounded-sm" title="Edit">
                          <Pencil size={10} className="text-zinc-500" />
                        </button>
                        <button onClick={() => deleteCustomCmd(c.id)} className="p-1 hover:bg-red-100 rounded-sm" title="Hapus" data-testid={`cmd-list-delete-${c.id}`}>
                          <Trash size={10} className="text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-zinc-200 bg-zinc-50">
              {editingCmd ? (
                <button data-testid="cmd-modal-delete" onClick={() => { deleteCustomCmd(editingCmd.id); setCmdModalOpen(false); }}
                  className="text-[11px] px-2 py-1 text-red-600 hover:bg-red-50 rounded-sm flex items-center gap-1">
                  <Trash size={11} /> Hapus
                </button>
              ) : <div />}
              <div className="flex gap-2">
                <button onClick={() => setCmdModalOpen(false)} className="text-[11px] px-3 py-1.5 text-zinc-600 hover:bg-zinc-100 rounded-sm">
                  Batal
                </button>
                <Button data-testid="cmd-modal-save" onClick={saveCustomCmd}
                  className="text-[11px] h-7 px-3 bg-violet-600 hover:bg-violet-700 text-white gap-1">
                  <FloppyDisk size={11} /> {editingCmd ? "Simpan" : "Tambah"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
